import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  FiX, FiPlus, FiTrash2, FiUsers, FiSettings, 
  FiRefreshCw, FiChevronDown, FiMenu, FiAlertTriangle, 
  FiSearch, FiAlertCircle, FiCheckCircle, FiActivity, 
  FiLayers, FiGrid, FiList, FiEye,   
  FiCpu, FiEdit2, FiUserPlus, FiUserX, FiClock
} from 'react-icons/fi';
import api from '../../../api';

const noopNotify = (msg, type) => {
  if (typeof window !== "undefined") {
    window.alert(`${type ? `[${type}] ` : ""}${msg}`);
  }
};

const LabBatchManagementModal = ({ 
  isOpen, onClose, classId, className, tenantId: propTenantId,
  userId, organizationId, notify, labSubjects = [],
  isSemesterBased: propIsSemesterBased, semesterId: propSemesterId,
  termId: propTermId, semesterNumber: propSemesterNumber, termNumber: propTermNumber
}) => {
  const tenantId = propTenantId || localStorage.getItem('tenantId');
  const notifyFn = notify || noopNotify;
  
  // Core State
  const [labBatches, setLabBatches] = useState([]);
  const [unassignedStudents, setUnassignedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('number');
  const [showBatchControls, setShowBatchControls] = useState(false);
  
  const [classInfo, setClassInfo] = useState({
    isSemesterBased: propIsSemesterBased !== undefined ? propIsSemesterBased : true,
    semesterId: propSemesterId || null,
    termId: propTermId || null,
    semesterNumber: propSemesterNumber || null,
    termNumber: propTermNumber || null
  });

  // Student Management State
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [selectedBatchStudents, setSelectedBatchStudents] = useState([]);
  const [removingStudent, setRemovingStudent] = useState(null);
  const [currentBatchForStudents, setCurrentBatchForStudents] = useState(null);

  // Unassigned Student State
  const [showUnassignedModal, setShowUnassignedModal] = useState(false);
  const [selectedUnassigned, setSelectedUnassigned] = useState([]);
  const [targetAssignBatchId, setTargetAssignBatchId] = useState('');
  const [assigningLoading, setAssigningLoading] = useState(false);
  
  // Move/Transfer State
  const [showMoveStudentsModal, setShowMoveStudentsModal] = useState(false);
  const [moveStudentsMode, setMoveStudentsMode] = useState(false);
  const [moveSourceBatch, setMoveSourceBatch] = useState(null);
  const [moveTargetBatchId, setMoveTargetBatchId] = useState('');
  const [moveStudentsList, setMoveStudentsList] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [moveSearch, setMoveSearch] = useState('');
  const [moveAvailableBatches, setMoveAvailableBatches] = useState([]);
  const moveTargetBatchIdRef = useRef('');

  // Faculty Assignment State - BATCH SPECIFIC
  const [showFacultyAssignmentModal, setShowFacultyAssignmentModal] = useState(false);
  const [selectedBatchForFaculty, setSelectedBatchForFaculty] = useState(null);
  const [selectedSubjectForFaculty, setSelectedSubjectForFaculty] = useState(null);
  const [selectedSubjectNameForFaculty, setSelectedSubjectNameForFaculty] = useState('');
  const [availableFaculty, setAvailableFaculty] = useState([]);
  const [selectedFacultyIds, setSelectedFacultyIds] = useState([]);
  const [facultyLoading, setFacultyLoading] = useState(false);
  // Store faculty assignments with batch-specific keys: `${batchId}_${subjectId}`
  const [subjectFacultyMap, setSubjectFacultyMap] = useState({});

  const [syncing, setSyncing] = useState(false);
  const [newBatchForm, setNewBatchForm] = useState({
    batchCount: 2, batchPrefix: 'Lab Batch', defaultCapacity: 30
  });
  const [classBatchSettings, setClassBatchSettings] = useState({
    targetBatchCount: 0, currentCapacity: 30, newCapacity: 30, batchPrefix: 'Lab Batch'
  });
  const [adjustingBatchCount, setAdjustingBatchCount] = useState(false);
  const [updatingClassCapacity, setUpdatingClassCapacity] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false, title: '', message: '', onConfirm: null, type: 'warning', confirmText: 'Confirm', loading: false
  });

  useEffect(() => {
    moveTargetBatchIdRef.current = moveTargetBatchId;
  }, [moveTargetBatchId]);

  // Analytics
  const analytics = useMemo(() => {
    const totalBatches = labBatches.length;
    const totalCapacity = labBatches.reduce((acc, curr) => acc + (curr.maxStudents || 0), 0);
    const enrolledStudents = labBatches.reduce((acc, curr) => acc + (curr.studentIds?.length || 0), 0);
    const unassignedCount = unassignedStudents.length;
    const utilizationRate = totalCapacity > 0 ? (enrolledStudents / totalCapacity) * 100 : 0;
    const availableSeats = totalCapacity - enrolledStudents;
    
    let emptyCount = 0, underUtilizedCount = 0, optimalCount = 0, fullCount = 0;
    labBatches.forEach(b => {
      const count = b.studentIds?.length || 0;
      const cap = b.maxStudents || 1;
      const ratio = count / cap;
      if (count === 0) emptyCount++;
      else if (ratio < 0.5) underUtilizedCount++;
      else if (ratio >= 0.9) fullCount++;
      else optimalCount++;
    });

    return {
      totalBatches, enrolledStudents, unassignedCount, totalCapacity,
      utilizationRate: utilizationRate.toFixed(1), availableSeats,
      emptyCount, underUtilizedCount, optimalCount, fullCount,
      systemType: classInfo.isSemesterBased ? 'Semester' : 'Term',
      periodDisplay: classInfo.isSemesterBased ? `Sem ${classInfo.semesterNumber || ''}` : `Term ${classInfo.termNumber || ''}`
    };
  }, [labBatches, unassignedStudents, classInfo]);

  const filteredBatches = useMemo(() => {
    let filtered = labBatches.filter(batch => 
      batch.batchPrefix?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      batch.batchNumber?.toString().includes(searchTerm)
    );
    filtered.sort((a, b) => {
      if (sortBy === 'utilization') return ((b.studentIds?.length || 0) / b.maxStudents) - ((a.studentIds?.length || 0) / a.maxStudents);
      if (sortBy === 'students') return (b.studentIds?.length || 0) - (a.studentIds?.length || 0);
      return (a.batchNumber || 0) - (b.batchNumber || 0);
    });
    return filtered;
  }, [labBatches, searchTerm, sortBy]);

  // API Functions
  const fetchUnassignedStudents = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await api.get(`/api/classManager/lab-batches/unassigned-students?classId=${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if(res.data.success) setUnassignedStudents(res.data.unassignedStudents || []);
    } catch (error) { console.error("Error fetching unassigned:", error); }
  }, [classId]);

  // Fetch faculty assignments for a specific batch - returns map with batch-specific keys
  const fetchBatchFacultyAssignments = useCallback(async (batchId) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await api.get(`/api/classManager/lab-batches/${batchId}/subjects/faculty-assignments?tenantId=${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const facultyMap = {};
      (res.data.subjectFacultyAssignments || []).forEach(assignment => {
        const key = `${batchId}_${assignment.subjectId}`;
        facultyMap[key] = assignment.facultyIds || [];
      });
      return facultyMap;
    } catch (error) {
      console.error('Error fetching faculty assignments:', error);
      return {};
    }
  }, [tenantId]);

  // Fetch faculty already assigned to this lab subject in the class (global class level)
  const fetchFacultyForLabSubject = useCallback(async (subjectId) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await api.get(`/api/classManager/lab-subject-faculty/${classId}/${subjectId}?tenantId=${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableFaculty(res.data.faculty || []);
    } catch (error) {
      console.error('Error fetching faculty for lab subject:', error);
      setAvailableFaculty([]);
    }
  }, [classId, tenantId]);

  // Assign faculty to a specific batch subject
  const handleAssignFacultyToSubject = useCallback(async () => {
    if (!selectedBatchForFaculty || !selectedSubjectForFaculty || selectedFacultyIds.length === 0) {
      notifyFn('Please select faculty to assign', 'error');
      return;
    }

    setFacultyLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      await api.post(`/api/classManager/lab-batches/${selectedBatchForFaculty._id}/subjects/assign-faculty`, {
        subjectId: selectedSubjectForFaculty,
        facultyIds: selectedFacultyIds,
        tenantId: tenantId
      }, { headers: { Authorization: `Bearer ${token}` } });

      notifyFn('Faculty assigned successfully', 'success');
      
      const updatedMap = await fetchBatchFacultyAssignments(selectedBatchForFaculty._id);
      setSubjectFacultyMap(prev => ({ ...prev, ...updatedMap }));
      
      setSelectedFacultyIds([]);
      setShowFacultyAssignmentModal(false);
      setSelectedSubjectForFaculty(null);
      setSelectedSubjectNameForFaculty('');
    } catch (error) {
      console.error('Error assigning faculty:', error);
      notifyFn(error.response?.data?.message || 'Failed to assign faculty', 'error');
    } finally {
      setFacultyLoading(false);
    }
  }, [selectedBatchForFaculty, selectedSubjectForFaculty, selectedFacultyIds, tenantId, notifyFn, fetchBatchFacultyAssignments]);

  // Unassign faculty from a specific batch subject
  const handleUnassignFaculty = useCallback(async (batchId, subjectId, facultyId) => {
    try {
      const token = localStorage.getItem('authToken');
      await api.post(`/api/classManager/lab-batches/${batchId}/subjects/unassign-faculty`, {
        subjectId: subjectId,
        facultyId: facultyId,
        tenantId: tenantId
      }, { headers: { Authorization: `Bearer ${token}` } });

      notifyFn('Faculty unassigned successfully', 'success');
      
      const updatedMap = await fetchBatchFacultyAssignments(batchId);
      setSubjectFacultyMap(prev => ({ ...prev, ...updatedMap }));
    } catch (error) {
      console.error('Error unassigning faculty:', error);
      notifyFn(error.response?.data?.message || 'Failed to unassign faculty', 'error');
    }
  }, [tenantId, notifyFn, fetchBatchFacultyAssignments]);

  // Open faculty assignment modal for a specific batch and subject
  const openFacultyAssignmentModal = useCallback(async (batch, subjectId, subjectName) => {
    setSelectedBatchForFaculty(batch);
    setSelectedSubjectForFaculty(subjectId);
    setSelectedSubjectNameForFaculty(subjectName);
    
    const batchFacultyMap = await fetchBatchFacultyAssignments(batch._id);
    setSubjectFacultyMap(prev => ({ ...prev, ...batchFacultyMap }));
    
    await fetchFacultyForLabSubject(subjectId);
    
    const batchSubjectKey = `${batch._id}_${subjectId}`;
    const currentFaculty = batchFacultyMap[batchSubjectKey] || [];
    setSelectedFacultyIds(currentFaculty);
    
    setShowFacultyAssignmentModal(true);
  }, [fetchBatchFacultyAssignments, fetchFacultyForLabSubject]);

  // Fetch all lab batches
  const fetchLabBatches = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const batchRes = await api.get(`/api/classManager/lab-batches/${classId}?tenantId=${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const batches = batchRes.data.batches || [];
      setLabBatches(batches);
      
      if (batchRes.data.classInfo) {
        setClassInfo({
          isSemesterBased: batchRes.data.classInfo.isSemesterBased,
          semesterId: batchRes.data.classInfo.semesterId,
          termId: batchRes.data.classInfo.termId,
          semesterNumber: batchRes.data.classInfo.semesterNumber,
          termNumber: batchRes.data.classInfo.termNumber
        });
      }
      
      if (batches && batches.length > 0) {
        const firstBatch = batches[0];
        setClassBatchSettings(prev => ({
          ...prev,
          targetBatchCount: batches.length,
          currentCapacity: firstBatch.maxStudents || 30,
          newCapacity: firstBatch.maxStudents || 30,
          batchPrefix: firstBatch.batchPrefix || 'Lab Batch'
        }));
        
        const allFacultyMaps = {};
        for (const batch of batches) {
          const facultyMap = await fetchBatchFacultyAssignments(batch._id);
          Object.assign(allFacultyMaps, facultyMap);
        }
        setSubjectFacultyMap(allFacultyMaps);
      }

      await fetchUnassignedStudents();
    } catch (error) {
      console.error('Error fetching data:', error);
      notifyFn('Failed to fetch batch data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [classId, tenantId, notifyFn, fetchUnassignedStudents, fetchBatchFacultyAssignments]);

  // Create batches
  const handleCreateBatches = async () => {
    if (newBatchForm.batchCount <= 0 || newBatchForm.defaultCapacity <= 0 || !newBatchForm.batchPrefix.trim()) {
      notifyFn('Please check your inputs. Values must be positive.', 'error');
      return;
    }
    setCreating(true);
    try {
      const token = localStorage.getItem('authToken');
      const maxBatchNumber = labBatches.length > 0 ? Math.max(...labBatches.map(b => b.batchNumber || 0)) : 0;
      const payload = {
        classId, batchCount: newBatchForm.batchCount, tenantId, organizationId,
        createdBy: userId, defaultCapacity: Number(newBatchForm.defaultCapacity),
        batchPrefix: newBatchForm.batchPrefix, startingBatchNumber: maxBatchNumber + 1,
        isSemesterBased: classInfo.isSemesterBased, semesterId: classInfo.semesterId, termId: classInfo.termId
      };
      await api.post('/api/classManager/lab-batches', payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      notifyFn(`${newBatchForm.batchCount} batches created!`, 'success');
      setNewBatchForm({ batchCount: 2, batchPrefix: 'Lab Batch', defaultCapacity: 30 });
      await fetchLabBatches();
    } catch (error) { 
      notifyFn(error.response?.data?.message || 'Creation failed', 'error');
    } finally { 
      setCreating(false);
    }
  };

  // Delete batch
  const handleDeleteBatch = async (batchId, batchNumber) => {
    const batch = labBatches.find(b => b._id === batchId);
    const studentCount = batch?.studentIds?.length || 0;
    if (studentCount > 0) {
      setMoveStudentsMode(true);
      const availableBatches = labBatches.filter(b => b._id !== batchId && b.isActive && (b.maxStudents - (b.studentIds?.length || 0)) >= studentCount);
      if (availableBatches.length === 0) {
        notifyFn('No space in other batches to move students.', 'error');
        return;
      }
      setMoveAvailableBatches(availableBatches);
      setMoveTargetBatchId(availableBatches[0]._id);
      showConfirmModal(`Move Students & Delete Batch #${batchNumber}`, '', async () => {
        setConfirmModal(p => ({ ...p, loading: true }));
        try {
          const token = localStorage.getItem('authToken');
          await api.put(`/api/classManager/move-students-between-batches`, 
            { fromBatchId: batchId, toBatchId: moveTargetBatchIdRef.current, studentIds: batch.studentIds },
            { headers: { Authorization: `Bearer ${token}` } });
          await api.delete(`/api/classManager/lab-batches/${batchId}`, { headers: { Authorization: `Bearer ${token}` } });
          notifyFn(`Batch #${batchNumber} deleted securely.`, 'success');
          await fetchLabBatches();
        } catch (error) { notifyFn('Operation failed.', 'error'); }
        finally { setDeleting(null); closeConfirmModal(); }
      }, 'warning');
    } else {
      setMoveStudentsMode(false);
      showConfirmModal(`Delete Batch #${batchNumber}?`, `This batch is empty. Confirm deletion?`, async () => {
        setConfirmModal(p => ({ ...p, loading: true }));
        try {
          const token = localStorage.getItem('authToken');
          await api.delete(`/api/classManager/lab-batches/${batchId}`, { headers: { Authorization: `Bearer ${token}` } });
          notifyFn(`Batch #${batchNumber} removed.`, 'success');
          await fetchLabBatches();
        } catch (error) { notifyFn('Deletion failed.', 'error'); }
        finally { setDeleting(null); closeConfirmModal(); }
      }, 'error', 'Delete');
    }
  };

  // Batch count adjustment
  const handleClassBatchCountAdjustment = async () => {
    const currentCount = labBatches.length;
    const targetCount = classBatchSettings.targetBatchCount;
    if (targetCount <= 0) { notifyFn('Count must be positive.', 'error'); return; }
    if (targetCount === currentCount) { notifyFn('No change in count.', 'info'); return; }
    const isReduction = targetCount < currentCount;
    if (isReduction) {
      const emptyBatches = labBatches.filter(b => (b.studentIds?.length || 0) === 0);
      if (emptyBatches.length < (currentCount - targetCount)) {
        notifyFn(`Cannot reduce. Need ${currentCount - targetCount} empty batches.`, 'error');
        return;
      }
    }
    showConfirmModal(isReduction ? 'Reduce Batches' : 'Increase Batches', 
      isReduction ? `Removing ${currentCount - targetCount} empty batches.` : `Adding ${targetCount - currentCount} new batches.`,
      async () => {
        setAdjustingBatchCount(true);
        setConfirmModal(p => ({ ...p, loading: true }));
        try {
          const token = localStorage.getItem('authToken');
          await api.put(`/api/classManager/classes/${classId}/batch-count`, {
            targetBatchCount: targetCount, tenantId, organizationId, userId,
            bulkSettings: { defaultCapacity: classBatchSettings.currentCapacity, batchPrefix: classBatchSettings.batchPrefix }
          }, { headers: { Authorization: `Bearer ${token}` } });
          notifyFn('Batch configuration updated.', 'success');
          await fetchLabBatches();
        } catch (e) { notifyFn('Update failed.', 'error'); }
        finally { setAdjustingBatchCount(false); closeConfirmModal(); }
      }, toggleTypeHelper(isReduction));
  };

  const toggleTypeHelper = (status) => status ? 'warning' : 'info';

  // Update all batch capacities
  const handleUpdateClassCapacity = async () => {
    const newCap = classBatchSettings.newCapacity;
    if (newCap <= 0) { notifyFn('Capacity must be positive.', 'error'); return; }
    const conflicts = labBatches.filter(b => (b.studentIds?.length || 0) > newCap);
    showConfirmModal('Global Capacity Update', 
      `Setting ALL batches to ${newCap} capacity.${conflicts.length > 0 ? `\n\n⚠️ ${conflicts.length} batches will be over-capacity.` : ''}`,
      async () => {
        setUpdatingClassCapacity(true);
        setConfirmModal(p => ({ ...p, loading: true }));
        try {
          const token = localStorage.getItem('authToken');
          await Promise.all(labBatches.map(b => api.put(`/api/classManager/lab-batches/${b._id}`, {
            maxStudents: newCap, batchPrefix: b.batchPrefix, isActive: b.isActive
          }, { headers: { Authorization: `Bearer ${token}` } })));
          setClassBatchSettings(p => ({ ...p, currentCapacity: newCap }));
          await fetchLabBatches();
          notifyFn('Capacity updated globally.', 'success');
        } catch (e) { notifyFn('Update failed.', 'error'); }
        finally { setUpdatingClassCapacity(false); closeConfirmModal(); }
      }, conflicts.length > 0 ? 'warning' : 'info');
  };

  // Assign unassigned students
  const handleAssignUnassignedStudents = async () => {
    if(!targetAssignBatchId || selectedUnassigned.length === 0) {
      notifyFn("Select students and a target batch.", "error");
      return;
    }
    setAssigningLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      await api.post(`/api/classManager/assign-students-to-lab-batch`, {
        studentIds: selectedUnassigned, classId: classId, batchId: targetAssignBatchId, tenantId: tenantId
      }, { headers: { Authorization: `Bearer ${token}` } });
      notifyFn(`${selectedUnassigned.length} students assigned successfully.`, 'success');
      setShowUnassignedModal(false);
      setSelectedUnassigned([]);
      setTargetAssignBatchId('');
      await fetchLabBatches();
    } catch(e) { notifyFn("Failed to assign students.", "error"); }
    finally { setAssigningLoading(false); }
  };

  // Update individual batch capacity
  const handleUpdateIndividualBatch = async (batchId, newCapacity) => {
    try {
      const token = localStorage.getItem('authToken');
      const batch = labBatches.find(b => b._id === batchId);
      await api.put(`/api/classManager/lab-batches/${batchId}`, {
        maxStudents: parseInt(newCapacity), batchPrefix: batch.batchPrefix, isActive: batch.isActive
      }, { headers: { Authorization: `Bearer ${token}` } });
      notifyFn(`Batch capacity updated to ${newCapacity}`, 'success');
      await fetchLabBatches();
    } catch(e) { notifyFn("Failed to update batch capacity", "error"); }
  };

  // Remove student from batch
  const handleRemoveStudent = async (batchId, studentId, name) => {
    if (!tenantId) {
      notifyFn('Tenant ID missing. Please refresh and try again.', 'error');
      return;
    }
    showConfirmModal('Unassign Student', `Remove ${name} from this batch? They will move to unassigned list.`, async () => {
      setConfirmModal(p => ({ ...p, loading: true }));
      setRemovingStudent(studentId);
      try {
        await api.delete(`/api/classManager/lab-batches/${batchId}/students/${studentId}`, {
          headers: { "Content-Type": "application/json" }, data: { tenantId, classId }
        });
        notifyFn('Student unassigned.', 'success');
        await fetchLabBatches();
        if (showStudentsModal && currentBatchForStudents) await fetchStudentsInBatch(currentBatchForStudents);
      } catch (e) { notifyFn('Failed to remove.', 'error'); }
      finally { setRemovingStudent(null); closeConfirmModal(); }
    }, 'warning');
  };

  // Fetch students in batch
  const fetchStudentsInBatch = async (batch) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await api.get(`/api/classManager/lab-batches/${batch._id}/students?classId=${classId}&tenantId=${tenantId}`, 
        { headers: { Authorization: `Bearer ${token}` } });
      setSelectedBatchStudents(sortStudentsByUSN(res.data.students));
      setCurrentBatchForStudents(batch);
    } catch (error) { notifyFn('Failed to load student list.', 'error'); }
  };

  const handleViewStudents = async (batch) => {
    setShowStudentsModal(true);
    await fetchStudentsInBatch(batch);
  };

  useEffect(() => { if (isOpen && classId) fetchLabBatches(); }, [isOpen, classId, fetchLabBatches]);
  useEffect(() => { if (labBatches.length > 0) setClassBatchSettings(p => ({ ...p, targetBatchCount: labBatches.length })); }, [labBatches]);

  const showConfirmModal = (title, message, onConfirm, type = 'warning', confirmText='Confirm') => {
    setConfirmModal({ isOpen: true, title, message, onConfirm, type, confirmText, loading: false });
  };
  const closeConfirmModal = () => setConfirmModal(p => ({ ...p, isOpen: false }));
  const toggleBatchDetails = (id) => {
    const s = new Set(expandedBatches);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpandedBatches(s);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-0 sm:p-4 z-[9999] font-sans">
      <div className="bg-slate-100 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-[1400px] h-[95vh] flex flex-col overflow-hidden border border-slate-200">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-950 to-indigo-950 px-4 sm:px-6 py-4 flex justify-between items-center sticky top-0 z-10 text-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 border border-white/10 rounded-xl flex items-center justify-center">
              <FiLayers className="text-white text-base sm:text-lg" />
            </div>
            <div>
              <h1 className="font-black text-base sm:text-lg tracking-tight uppercase">{className}</h1>
              <p className="text-slate-300 text-xs font-semibold">{analytics.systemType} Plan &bull; {analytics.periodDisplay}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
            >
              <FiMenu size={18} />
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl border border-slate-700 hover:bg-slate-800 text-slate-300 hover:text-white transition-colors">
              <FiX size={16} />
            </button>
          </div>
        </div>

        {/* Mobile Sidebar/Drawer Menu */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-20 bg-slate-950/60" onClick={() => setMobileMenuOpen(false)}>
            <div className="absolute right-0 top-0 h-full w-80 bg-slate-100 shadow-2xl flex flex-col border-l border-slate-200" onClick={e => e.stopPropagation()}>
              <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                <h3 className="font-bold text-xs uppercase tracking-wider text-slate-500">Summary Details</h3>
                <button onClick={() => setMobileMenuOpen(false)} className="w-7 h-7 flex items-center justify-center border border-slate-300 bg-white text-slate-600 rounded-lg"><FiX size={14} /></button>
              </div>
              <div className="p-4 space-y-4 overflow-y-auto flex-1 pb-20">
                <MobileAnalyticsPanel analytics={analytics} />
              </div>
            </div>
          </div>
        )}

        {/* Master Content Section Splitter */}
        <div className="flex flex-1 overflow-hidden">
          {/* Desktop Left Fixed Summary Bar */}
          <div className="hidden lg:block w-72 bg-slate-50 border-r border-slate-200 overflow-y-auto">
            <DesktopAnalyticsPanel analytics={analytics} onConfigure={() => setShowBatchControls(true)} />
          </div>

          {/* Main Batches Workspace Grid */}
          <div className="flex-1 flex flex-col overflow-hidden bg-slate-100">
            {/* Action Tools Row */}
            <div className="bg-white border-b border-slate-200 px-4 py-3 flex flex-wrap gap-3 items-center justify-between sticky top-0 z-10">
              <div className="relative flex-1 min-w-[200px]">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input 
                  type="text" placeholder="Search lab groups..." value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-600/10 focus:border-indigo-600"
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="flex bg-slate-200 p-1 rounded-xl border border-slate-300/40">
                  <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white shadow-xs text-indigo-600 font-bold' : 'text-slate-500'}`}>
                    <FiGrid size={15} />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-xs text-indigo-600 font-bold' : 'text-slate-500'}`}>
                    <FiList size={15} />
                  </button>
                </div>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700">
                  <option value="number">Sort: Group Number</option>
                  <option value="utilization">Sort: Seats Taken</option>
                  <option value="students">Sort: Total Enrolled</option>
                </select>
                <button onClick={async () => {
                  setSyncing(true);
                  try {
                    const token = localStorage.getItem('authToken');
                    await api.post(`/api/classManager/sync-lab-batch-assignments/${classId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                    notifyFn('Synced successfully', 'success');
                    fetchLabBatches();
                  } catch(e) { notifyFn('Sync failed', 'error'); }
                  finally { setSyncing(false); }
                }} className="px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-700 hover:bg-slate-100 transition-colors flex items-center gap-1 shadow-xs">
                  <FiRefreshCw className={syncing ? 'animate-spin text-indigo-600' : ''} size={13} /> Sync List
                </button>
              </div>
            </div>

            {/* Dynamic Grid View Scroll Box */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loading ? (
                <div className="flex flex-col items-center justify-center h-64 space-y-2 bg-white rounded-xl border border-slate-200">
                  <div className="w-7 h-7 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-slate-500 text-xs font-semibold">Loading lab groups...</p>
                </div>
              ) : labBatches.length === 0 ? (
                <EmptyState newBatchForm={newBatchForm} setNewBatchForm={setNewBatchForm} handleCreateBatches={handleCreateBatches} creating={creating} />
              ) : (
                <>
                  {viewMode === 'grid' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pb-20">
                      {filteredBatches.map(batch => (
                        <BatchCard 
                          key={batch._id} 
                          batch={batch} 
                          expanded={expandedBatches.has(batch._id)}
                          onToggle={() => toggleBatchDetails(batch._id)} 
                          onDelete={() => handleDeleteBatch(batch._id, batch.batchNumber)}
                          onUpdateCapacity={handleUpdateIndividualBatch} 
                          deleting={deleting === batch._id}
                          setMoveSourceBatch={setMoveSourceBatch} 
                          setShowMoveStudentsModal={setShowMoveStudentsModal}
                          setSelectedStudents={setSelectedStudents} 
                          setMoveTargetBatchId={setMoveTargetBatchId}
                          setMoveSearch={setMoveSearch} 
                          setMoveStudentsList={setMoveStudentsList}
                          onViewStudents={() => handleViewStudents(batch)} 
                          onOpenFacultyModal={openFacultyAssignmentModal}
                          subjectFacultyMap={subjectFacultyMap} 
                          labSubjects={labSubjects}
                          batchId={batch._id}
                        />
                      ))}
                    </div>
                  )}
                  {viewMode === 'list' && (
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-xs font-medium">
                          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            <tr>
                              <th className="px-4 py-3">Lab Group</th>
                              <th className="px-4 py-3">Seats Filled</th>
                              <th className="px-4 py-3">Status</th>
                              <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200 text-slate-700 font-semibold">
                            {filteredBatches.map(batch => (
                              <BatchRow 
                                key={batch._id} 
                                batch={batch} 
                                onDelete={() => handleDeleteBatch(batch._id, batch.batchNumber)}
                                deleting={deleting === batch._id} 
                                onViewStudents={() => handleViewStudents(batch)}
                                onOpenFacultyModal={openFacultyAssignmentModal}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Sub-Modals Tree Stack (All set with z-[9999] floating blocks) */}
      <ConfirmationModal isOpen={confirmModal.isOpen} onClose={closeConfirmModal} onConfirm={confirmModal.onConfirm} 
        title={confirmModal.title} message={confirmModal.message} type={confirmModal.type} confirmLoading={confirmModal.loading}
        moveAvailableBatches={moveAvailableBatches} moveTargetBatchId={moveTargetBatchId} setMoveTargetBatchId={setMoveTargetBatchId}
        moveStudentsMode={moveStudentsMode} />

      {showBatchControls && (
        <SettingsPanel 
          labBatches={labBatches} classBatchSettings={classBatchSettings} setClassBatchSettings={setClassBatchSettings}
          handleClassBatchCountAdjustment={handleClassBatchCountAdjustment} adjustingBatchCount={adjustingBatchCount}
          handleUpdateClassCapacity={handleUpdateClassCapacity} updatingClassCapacity={updatingClassCapacity}
          onClose={() => setShowBatchControls(false)}
        />
      )}

      {showUnassignedModal && (
        <UnassignedModal 
          unassignedStudents={unassignedStudents} selectedUnassigned={selectedUnassigned}
          setSelectedUnassigned={setSelectedUnassigned} labBatches={labBatches} targetAssignBatchId={targetAssignBatchId}
          setTargetAssignBatchId={setTargetAssignBatchId} handleAssignUnassignedStudents={handleAssignUnassignedStudents}
          assigningLoading={assigningLoading} onClose={() => setShowUnassignedModal(false)}
        />
      )}

      {showStudentsModal && (
        <StudentListModal 
          currentBatchForStudents={currentBatchForStudents} selectedBatchStudents={selectedBatchStudents}
          removingStudent={removingStudent} handleRemoveStudent={handleRemoveStudent}
          onClose={() => setShowStudentsModal(false)}
        />
      )}

      {showMoveStudentsModal && (
        <MoveStudentsModal 
          moveSourceBatch={moveSourceBatch} moveStudentsList={moveStudentsList} moveSearch={moveSearch}
          setMoveSearch={setMoveSearch} selectedStudents={selectedStudents} setSelectedStudents={setSelectedStudents}
          labBatches={labBatches} moveTargetBatchId={moveTargetBatchId} setMoveTargetBatchId={setMoveTargetBatchId}
          onConfirm={async () => {
            try {
              const token = localStorage.getItem('authToken');
              await api.put(`/api/classManager/move-students-between-batches`, 
                { fromBatchId: moveSourceBatch._id, toBatchId: moveTargetBatchId, studentIds: selectedStudents },
                { headers: { Authorization: `Bearer ${token}` } });
              notifyFn('Transferred successfully.', 'success');
              setShowMoveStudentsModal(false);
              fetchLabBatches();
            } catch(e) { notifyFn('Transfer failed.', 'error'); }
          }}
          onClose={() => setShowMoveStudentsModal(false)}
        />
      )}

      {showFacultyAssignmentModal && selectedBatchForFaculty && (
        <FacultyAssignmentModal 
          batch={selectedBatchForFaculty} 
          subjectName={selectedSubjectNameForFaculty}
          availableFaculty={availableFaculty} 
          selectedFacultyIds={selectedFacultyIds}
          setSelectedFacultyIds={setSelectedFacultyIds} 
          subjectFacultyMap={subjectFacultyMap}
          selectedSubjectId={selectedSubjectForFaculty} 
          batchId={selectedBatchForFaculty._id}
          handleUnassignFaculty={handleUnassignFaculty}
          handleAssignFacultyToSubject={handleAssignFacultyToSubject} 
          facultyLoading={facultyLoading}
          onClose={() => setShowFacultyAssignmentModal(false)}
        />
      )}
    </div>
  );
};

// Helper Components
const sortStudentsByUSN = (students) => {
  return (students || []).slice().sort((a, b) => {
    const usnA = (a.usn || a.USN || a.studentId || a.registrationNumber || '').toUpperCase();
    const usnB = (b.usn || b.USN || b.studentId || b.registrationNumber || '').toUpperCase();
    return usnA.localeCompare(usnB, undefined, { numeric: true, sensitivity: 'base' });
  });
};

const DesktopAnalyticsPanel = ({ analytics, onConfigure }) => (
  <div className="p-4 space-y-4">
    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3.5 shadow-xs">
      <div className="flex items-center gap-2 text-indigo-700 mb-1 font-bold text-xs">
        <FiClock size={14} /> <span>{analytics.systemType} Plan</span>
      </div>
      <p className="text-[11px] font-semibold text-indigo-600">{analytics.periodDisplay}</p>
    </div>

    {analytics.unassignedCount > 0 ? (
      <div className="bg-amber-50 rounded-xl p-3.5 border border-amber-200 shadow-xs">
        <div className="flex justify-between items-start mb-2">
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Unassigned</p>
            <p className="text-xl font-black text-amber-900">{analytics.unassignedCount} Students</p>
          </div>
          <button 
            onClick={() => {
              const el = document.querySelector('[data-assign-modal]');
              if (el) el.click();
            }} 
            className="px-2.5 py-1 bg-white text-amber-800 border border-amber-300 rounded-xl text-xs font-bold hover:bg-amber-50 shadow-xs"
          >
            Assign
          </button>
        </div>
        <div className="h-1.5 bg-amber-200 rounded-full overflow-hidden">
          <div className="h-full bg-amber-600 rounded-full w-2/3 animate-pulse"></div>
        </div>
      </div>
    ) : (
      <div className="bg-emerald-50 rounded-xl p-3.5 border border-emerald-200 shadow-xs flex items-center gap-2.5">
        <FiCheckCircle className="text-emerald-600 text-lg flex-shrink-0" />
        <div className="text-xs">
          <p className="font-bold text-emerald-900">All Assigned</p>
          <p className="text-slate-500 font-medium">No pending students left</p>
        </div>
      </div>
    )}

    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs space-y-3">
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Seats Taken</p>
        <div className="flex items-baseline gap-1 mt-0.5">
          <span className="text-2xl font-black text-slate-900">{analytics.enrolledStudents}</span>
          <span className="text-xs font-bold text-slate-500">/ {analytics.totalCapacity} Seats</span>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-2 text-[10px] font-bold text-slate-600 bg-slate-50 p-2 rounded-lg border border-slate-200/60">
        <div>Pending: {analytics.unassignedCount}</div>
        <div>Empty: {analytics.availableSeats}</div>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-[11px] font-bold">
          <span className="text-slate-500">Seats Filled</span>
          <span className={analytics.utilizationRate > 90 ? 'text-amber-600 font-extrabold' : 'text-emerald-700'}>{analytics.utilizationRate}%</span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden border border-slate-300/20">
          <div className={`h-full rounded-full transition-all ${analytics.utilizationRate > 90 ? 'bg-amber-500' : 'bg-indigo-600'}`} 
            style={{ width: `${Math.min(analytics.utilizationRate, 100)}%` }}></div>
        </div>
      </div>
    </div>

    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs">
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
        <FiActivity size={13} /> Group Metrics
      </p>
      <div className="grid grid-cols-2 gap-2 text-center">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
          <p className="text-base font-black text-slate-800">{analytics.underUtilizedCount}</p>
          <p className="text-[9px] font-bold text-slate-400 uppercase">Low Fill</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
          <p className="text-base font-black text-slate-800">{analytics.optimalCount}</p>
          <p className="text-[9px] font-bold text-slate-400 uppercase">Good</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
          <p className="text-base font-black text-slate-800">{analytics.fullCount}</p>
          <p className="text-[9px] font-bold text-slate-400 uppercase">Full</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
          <p className="text-base font-black text-slate-800">{analytics.emptyCount}</p>
          <p className="text-[9px] font-bold text-slate-400 uppercase">Empty</p>
        </div>
      </div>
    </div>

    <button onClick={onConfigure} className="w-full py-3 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-slate-800 transition-all flex items-center justify-center gap-1.5 shadow-xs">
      <FiSettings size={14} /> Adjust Groups
    </button>
  </div>
);

const MobileAnalyticsPanel = ({ analytics }) => (
  <div className="space-y-4">
    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex items-center gap-2">
      <FiClock className="text-indigo-600" size={14} /> 
      <span className="text-xs font-bold text-indigo-900">{analytics.systemType} Plan &bull; {analytics.periodDisplay}</span>
    </div>
    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs grid grid-cols-2 gap-4">
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase">Enrolled Total</p>
        <p className="text-xl font-black text-slate-800">{analytics.enrolledStudents} / {analytics.totalCapacity}</p>
      </div>
      <div className="text-right">
        <p className="text-[10px] font-bold text-slate-400 uppercase">Total Taken</p>
        <p className="text-xl font-black text-indigo-600">{analytics.utilizationRate}%</p>
      </div>
    </div>
    {analytics.unassignedCount > 0 && (
      <div className="bg-amber-50 text-amber-950 border border-amber-200 rounded-xl p-3 text-xs font-bold flex items-center gap-1.5">
        <FiAlertCircle className="text-amber-700" />
        <span>{analytics.unassignedCount} Students Pending Assignment</span>
      </div>
    )}
  </div>
);

const EmptyState = ({ newBatchForm, setNewBatchForm, handleCreateBatches, creating }) => (
  <div className="max-w-md mx-auto mt-10 text-center space-y-4">
    <div className="w-16 h-16 bg-indigo-50 border border-indigo-100 rounded-full flex items-center justify-center mx-auto shadow-xs">
      <FiCpu className="text-2xl text-indigo-600" />
    </div>
    <div>
      <h3 className="text-base font-bold text-slate-900 uppercase tracking-wide">No Lab Groups Setup</h3>
      <p className="text-xs text-slate-500 font-medium mt-0.5">Please configure your initial laboratory groups to start.</p>
    </div>
    <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 text-left space-y-4">
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Number of Groups</label>
          <input type="number" value={newBatchForm.batchCount} onChange={e => setNewBatchForm(p => ({...p, batchCount: e.target.value}))}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-semibold text-slate-900" />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Seats per Group</label>
          <input type="number" value={newBatchForm.defaultCapacity} onChange={e => setNewBatchForm(p => ({...p, defaultCapacity: e.target.value}))}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-semibold text-slate-900" />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Group Prefix Name</label>
          <input type="text" value={newBatchForm.batchPrefix} onChange={e => setNewBatchForm(p => ({...p, batchPrefix: e.target.value}))}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-semibold text-slate-900" />
        </div>
        <button onClick={handleCreateBatches} disabled={creating}
          className="w-full py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-indigo-700 transition-colors shadow-sm">
          {creating ? 'Creating...' : 'Create Lab Groups Now'}
        </button>
      </div>
    </div>
  </div>
);

// Batch Card Component
const BatchCard = ({ batch, expanded, onToggle, onDelete, onUpdateCapacity, deleting, setMoveSourceBatch, 
  setShowMoveStudentsModal, setSelectedStudents, setMoveTargetBatchId, setMoveSearch, setMoveStudentsList, 
  onViewStudents, onOpenFacultyModal, subjectFacultyMap, labSubjects, batchId }) => {
  
  const filled = batch.studentIds?.length || 0;
  const [capacity, setCapacity] = useState(batch.maxStudents || 1);
  const [isEditingCap, setIsEditingCap] = useState(false);
  const utilization = Math.round((filled / capacity) * 100);
  const isFull = utilization >= 90;
  const isOver = filled > capacity;
  
  const handleSaveCapacity = async () => {
    await onUpdateCapacity(batch._id, capacity);
    setIsEditingCap(false);
  };

  const labSubjectsList = batch.labSubjectIds || [];
  const subjectsMap = Array.isArray(labSubjects) ? Object.fromEntries(labSubjects.map(s => [s._id || s.id, s.name])) : {};

  const getFacultyForSubject = (subjectId) => {
    const key = `${batchId}_${subjectId}`;
    return subjectFacultyMap[key] || [];
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-xs hover:shadow-sm transition-all overflow-hidden flex flex-col">
      <div className="p-4 flex-1 space-y-4">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl border flex items-center justify-center font-black text-sm flex-shrink-0 ${batch.isActive ? 'bg-indigo-50 border-indigo-100 text-indigo-700' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
              {batch.batchNumber}
            </div>
            <div className="min-w-0">
              <h4 className="font-bold text-slate-900 text-sm truncate">{batch.batchPrefix} {batch.batchNumber}</h4>
              <span className={`inline-block mt-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${batch.isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-slate-100 text-slate-500'}`}>
                {batch.isActive ? 'Active' : 'Archived'}
              </span>
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={onToggle} className="w-7 h-7 flex items-center justify-center border border-slate-200 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-indigo-600 transition-colors">
              <FiChevronDown className={`transform transition-transform ${expanded ? 'rotate-180' : ''}`} size={15} />
            </button>
            <button onClick={onDelete} disabled={deleting} className="w-7 h-7 flex items-center justify-center border border-slate-200 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600 transition-colors">
              {deleting ? '...' : <FiTrash2 size={14} />}
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between text-xs font-semibold text-slate-500">
            <span>Seats Filled</span>
            <span className={isOver ? 'text-rose-600 font-bold' : isFull ? 'text-amber-600' : 'text-slate-800'}>
              {filled} / {isEditingCap ? (
                <input type="number" value={capacity} onChange={(e) => setCapacity(e.target.value)} 
                  className="w-12 px-1 py-0.5 border border-indigo-300 rounded text-center text-xs bg-slate-50 font-bold" autoFocus />
              ) : (
                <span className="inline-flex items-center gap-1 font-bold">{capacity}
                  <button onClick={() => setIsEditingCap(true)} className="text-indigo-400 hover:text-indigo-600">
                    <FiEdit2 size={11} />
                  </button>
                </span>
              )}
            </span>
          </div>
          {isEditingCap && (
            <div className="flex gap-2 pt-0.5">
              <button onClick={handleSaveCapacity} className="flex-1 py-1 bg-emerald-600 text-white font-bold rounded text-[11px] shadow-xs">Save</button>
              <button onClick={() => { setIsEditingCap(false); setCapacity(batch.maxStudents); }} className="flex-1 py-1 bg-slate-200 text-slate-700 font-bold rounded text-[11px]">Cancel</button>
            </div>
          )}
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden border border-slate-300/20">
            <div className={`h-full rounded-full transition-all ${isOver ? 'bg-rose-500' : isFull ? 'bg-amber-500' : 'bg-emerald-600'}`} 
              style={{ width: `${Math.min(utilization, 100)}%` }}></div>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="bg-slate-50 border-t border-slate-200 p-3 space-y-3 shrink-0">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={onViewStudents} className="flex items-center justify-center gap-1 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 hover:text-indigo-600 shadow-xs transition-colors">
              <FiEye size={13} /> View Members
            </button>
            <button onClick={async () => {
              setMoveSourceBatch(batch);
              setShowMoveStudentsModal(true);
              setSelectedStudents([]);
              setMoveTargetBatchId('');
              setMoveSearch('');
              try {
                const token = localStorage.getItem('authToken');
                const res = await api.get(`/api/classManager/lab-batches/${batch._id}/students?classId=${batch.classId}&tenantId=${batch.tenantId}`, 
                  { headers: { Authorization: `Bearer ${token}` } });
                setMoveStudentsList(res.data.students || []);
              } catch(e) {}
            }} className="flex items-center justify-center gap-1 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 hover:text-indigo-600 shadow-xs transition-colors">
              <FiSettings size={13} /> Shift Students
            </button>
          </div>
          
          {labSubjectsList.length > 0 && (
            <div className="pt-2 border-t border-slate-200 space-y-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Assigned Group Teachers</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {labSubjectsList.map(subjectId => {
                  const facultyIds = getFacultyForSubject(subjectId);
                  const subjectName = subjectsMap[subjectId] || subjectId.slice(-6);
                  return (
                    <div key={subjectId} className="flex items-center justify-between p-2 bg-white rounded-xl border border-slate-200/80 text-xs">
                      <div className="min-w-0 flex-1 pr-2">
                        <p className="font-bold text-slate-800 truncate">{subjectName}</p>
                        <p className="text-[10px] font-medium text-slate-500 mt-0.5">
                          {facultyIds.length === 0 ? 'No teachers linked' : `${facultyIds.length} teachers assigned`}
                        </p>
                      </div>
                      <button onClick={() => onOpenFacultyModal(batch, subjectId, subjectName)} 
                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-indigo-600 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 rounded-lg flex-shrink-0 transition-colors">
                        Link
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BatchRow = ({ batch, onDelete, deleting, onViewStudents, onOpenFacultyModal }) => {
  const filled = batch.studentIds?.length || 0;
  const capacity = batch.maxStudents || 1;
  const utilization = Math.round((filled / capacity) * 100);
  
  return (
    <tr className="hover:bg-slate-50/80 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-200/60 font-black text-slate-700 flex items-center justify-center text-xs">
            {batch.batchNumber}
          </div>
          <span className="font-bold text-slate-900">{batch.batchPrefix} {batch.batchNumber}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-[120px]">
          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden border border-slate-300/10">
            <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${Math.min(utilization, 100)}%` }}></div>
          </div>
          <span className="font-mono text-slate-500 font-bold">{filled}/{capacity}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${batch.isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-slate-100 text-slate-500'}`}> 
          {batch.isActive ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-1.5">
          <button onClick={onViewStudents} className="w-7 h-7 flex items-center justify-center border border-slate-200 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-indigo-600 transition-colors" title="View Students">
            <FiEye size={14} />
          </button>
          <button onClick={() => onOpenFacultyModal(batch, batch.labSubjectIds?.[0], 'Lab Subjects')} 
            className="w-7 h-7 flex items-center justify-center border border-slate-200 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-indigo-600 transition-colors" title="Assign Faculty">
            <FiUsers size={14} />
          </button>
          <button onClick={onDelete} disabled={deleting} className="w-7 h-7 flex items-center justify-center border border-slate-200 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600 transition-colors">
            {deleting ? '...' : <FiTrash2 size={14} />}
          </button>
        </div>
      </td>
    </tr>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, type, confirmLoading, moveStudentsMode, moveAvailableBatches, moveTargetBatchId, setMoveTargetBatchId }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-5 border border-slate-200 shadow-2xl space-y-4 text-center">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center mx-auto ${type === 'error' ? 'bg-rose-50 text-rose-600 border border-rose-100' : 'bg-amber-50 text-amber-600 border border-amber-100'}`}>
          <FiAlertTriangle size={20} />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-900 uppercase tracking-wide">{title}</h3>
          <div className="text-xs font-semibold text-slate-600 mt-1.5 leading-relaxed">
            {moveStudentsMode ? (
              <div className="space-y-3 text-left">
                <p className="text-amber-900 bg-amber-50 border border-amber-100 p-2.5 rounded-xl">This group is not empty. Please pick a destination group to shift the students into:</p>
                <select className="w-full p-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-600/10" value={moveTargetBatchId} onChange={e => setMoveTargetBatchId(e.target.value)}>
                  {moveAvailableBatches.map(b => <option key={b._id} value={b._id}>Batch {b.batchNumber} (Free Seats: {b.maxStudents - (b.studentIds?.length || 0)})</option>)}
                </select>
              </div>
            ) : message}
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="flex-1 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-700 font-bold text-xs uppercase tracking-wider transition-colors hover:bg-slate-200">Cancel</button>
          <button onClick={onConfirm} disabled={confirmLoading} className={`flex-1 py-2.5 rounded-xl text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-sm ${type === 'error' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-slate-900 hover:bg-slate-800'}`}>
            {confirmLoading ? 'Processing...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SettingsPanel = ({ labBatches, classBatchSettings, setClassBatchSettings, handleClassBatchCountAdjustment, adjustingBatchCount, handleUpdateClassCapacity, updatingClassCapacity, onClose }) => (
  <div className="fixed inset-y-0 right-0 z-[9999] lg:relative lg:inset-auto lg:w-96 bg-slate-100 shadow-2xl border-l border-slate-200 overflow-y-auto animate-slide-right flex flex-col w-80">
    <div className="sticky top-0 bg-slate-50 border-b border-slate-200 p-4 flex justify-between items-center z-10">
      <h2 className="font-bold text-xs uppercase tracking-wider text-slate-500">Group Configuration</h2>
      <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500"><FiX size={15} /></button>
    </div>
    <div className="p-4 space-y-5 flex-1 overflow-y-auto">
      <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs space-y-2">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Fast Action</p>
        <button onClick={() => setClassBatchSettings(p => ({ ...p, targetBatchCount: labBatches.length + 1 }))} 
          className="w-full py-2.5 bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-bold uppercase tracking-wider rounded-xl hover:bg-indigo-100 transition-colors flex items-center justify-center gap-1.5 shadow-sm">
          <FiPlus size={14} /> Add One Group
        </button>
      </div>
      
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs space-y-2.5">
        <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Total Group Count</label>
        <div className="flex gap-2">
          <input type="number" value={classBatchSettings.targetBatchCount} onChange={e => setClassBatchSettings(p => ({ ...p, targetBatchCount: parseInt(e.target.value) || 0 }))}
            className="flex-1 px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900" />
          <button onClick={handleClassBatchCountAdjustment} disabled={adjustingBatchCount}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm">
            Set
          </button>
        </div>
      </div>
      
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs space-y-3">
        <div className="space-y-1">
          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Global Group Size</label>
          <input type="number" value={classBatchSettings.newCapacity} onChange={e => setClassBatchSettings(p => ({ ...p, newCapacity: parseInt(e.target.value) || 0 }))}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900" />
        </div>
        <button onClick={handleUpdateClassCapacity} disabled={updatingClassCapacity}
          className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm transition-colors">
          Update Size Everywhere
        </button>
      </div>
    </div>
  </div>
);

const UnassignedModal = ({ unassignedStudents, selectedUnassigned, setSelectedUnassigned, labBatches, targetAssignBatchId, setTargetAssignBatchId, handleAssignUnassignedStudents, assigningLoading, onClose }) => (
  <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-[9999] flex items-center justify-center p-4">
    <div className="bg-slate-100 rounded-2xl w-full max-w-lg max-h-[82vh] flex flex-col border border-slate-200 shadow-2xl overflow-hidden">
      <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-amber-50 shrink-0">
        <h3 className="font-bold text-sm text-amber-900 uppercase tracking-wide flex items-center gap-1.5"><FiAlertCircle className="text-amber-700" /> Unassigned Students</h3>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg border border-amber-200 bg-white text-amber-700"><FiX size={14} /></button>
      </div>
      <div className="flex-1 overflow-auto p-4 bg-white">
        {unassignedStudents.length === 0 ? (
          <p className="text-center font-semibold text-slate-500 text-xs py-10">All students have been assigned to groups!</p>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between items-center border-b border-slate-100 pb-2">
              <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">{unassignedStudents.length} Students Pending</span>
              <button onClick={() => setSelectedUnassigned(unassignedStudents.map(s => s.studentId))} className="text-xs text-indigo-600 font-bold hover:underline">Select All</button>
            </div>
            <div className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1">
              {unassignedStudents.map(s => (
                <div key={s.studentId} onClick={() => setSelectedUnassigned(prev => prev.includes(s.studentId) ? prev.filter(id => id !== s.studentId) : [...prev, s.studentId])}
                  className={`p-3 border rounded-xl cursor-pointer flex items-center gap-3 transition-colors ${selectedUnassigned.includes(s.studentId) ? 'bg-indigo-50/50 border-indigo-200 shadow-xs' : 'bg-slate-50/50 border-slate-200 hover:bg-slate-50'}`}>
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selectedUnassigned.includes(s.studentId) ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 bg-white'}`}>
                    {selectedUnassigned.includes(s.studentId) && <FiCheckCircle className="text-white text-[10px]" />}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-800 text-sm truncate">{s.name}</p>
                    <p className="text-[11px] font-mono font-bold text-indigo-600 mt-0.5">{s.usn || s.rollNumber || '--'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="p-4 bg-slate-50 border-t border-slate-200 space-y-3 shrink-0">
        <div className="space-y-1">
          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Target Lab Group</label>
          <div className="flex gap-2">
            <select className="flex-1 p-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-600/10" value={targetAssignBatchId} onChange={e => setTargetAssignBatchId(e.target.value)}>
              <option value="">Choose a group...</option>
              {labBatches.filter(b => b.isActive).map(b => {
                const free = b.maxStudents - (b.studentIds?.length || 0);
                return <option key={b._id} value={b._id} disabled={free <= 0}>Batch {b.batchNumber} ({free} free seats)</option>;
              })}
            </select>
            <button onClick={handleAssignUnassignedStudents} disabled={assigningLoading || !targetAssignBatchId || selectedUnassigned.length === 0}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider disabled:opacity-50 flex items-center gap-1 shadow-sm">
              {assigningLoading ? '...' : 'Assign'} <FiUserPlus size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const StudentListModal = ({ currentBatchForStudents, selectedBatchStudents, removingStudent, handleRemoveStudent, onClose }) => (
  <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-[9999] flex items-center justify-center p-4">
    <div className="bg-slate-100 rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col border border-slate-200 shadow-2xl overflow-hidden">
      <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 shrink-0">
        <div>
          <h3 className="font-bold text-sm text-slate-900 uppercase tracking-wider flex items-center gap-1.5"><FiUsers /> Group {currentBatchForStudents?.batchNumber} Students</h3>
          <p className="text-[11px] font-semibold text-slate-500 mt-0.5">Sorted Alphanumerically by USN</p>
        </div>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-500 hover:bg-slate-50"><FiX size={15} /></button>
      </div>
      <div className="flex-1 overflow-auto bg-white">
        <table className="w-full text-left border-collapse text-xs font-medium">
          <thead className="text-[10px] text-slate-500 uppercase bg-slate-50 border-b border-slate-200 font-bold tracking-wider">
            <tr>
              <th className="px-4 py-3 text-center w-16">Sl.No</th>
              <th className="px-4 py-3">USN</th>
              <th className="px-4 py-3">Student Name</th>
              <th className="px-4 py-3">Email Address</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700 font-semibold">
            {selectedBatchStudents.length === 0 ? (
              <tr><td colSpan="5" className="py-12 text-center font-bold text-slate-400">No students are currently linked to this group</td></tr>
            ) : (
              selectedBatchStudents.map((s, i) => (
                <tr key={i} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-4 py-3 text-center font-mono font-bold text-slate-400 bg-slate-50/20">{i + 1}</td>
                  <td className="px-4 py-3">
                    <span className="font-mono font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">
                      {s.usn || s.USN || s.registrationNumber || '--'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-bold text-slate-900">{s.firstName} {s.lastName}</td>
                  <td className="px-4 py-3 text-slate-500 max-w-[200px] truncate">{s.email || '--'}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleRemoveStudent(currentBatchForStudents?._id, s.studentId, `${s.firstName} ${s.lastName}`)} 
                      disabled={removingStudent === s.studentId}
                      className="text-rose-600 bg-rose-50 hover:bg-rose-100 px-2.5 py-1 rounded-lg border border-rose-100 text-[11px] font-bold uppercase tracking-wide flex items-center justify-center gap-1 ml-auto transition-colors">
                      {removingStudent === s.studentId ? '...' : <><FiUserX size={12} /> Remove</>}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="p-3 bg-slate-50 border-t border-slate-200 text-right shrink-0">
        <span className="text-[11px] font-bold text-slate-600 bg-slate-200 px-2.5 py-1 border border-slate-300 rounded-lg">
          Group Strength: {selectedBatchStudents.length}
        </span>
      </div>
    </div>
  </div>
);

const MoveStudentsModal = ({ moveSourceBatch, moveStudentsList, moveSearch, setMoveSearch, selectedStudents, setSelectedStudents, labBatches, moveTargetBatchId, setMoveTargetBatchId, onConfirm, onClose }) => (
  <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-[9999] flex items-center justify-center p-4">
    <div className="bg-slate-100 rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col border border-slate-200 shadow-2xl overflow-hidden">
      <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center shrink-0">
        <div>
          <h3 className="font-bold text-sm text-slate-900 uppercase tracking-wider">Transfer Students Between Groups</h3>
          <p className="text-[11px] font-semibold text-slate-500 mt-0.5">Shift members to alternative batches</p>
        </div>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-500 hover:bg-slate-50"><FiX size={15} /></button>
      </div>
      <div className="flex-1 overflow-hidden flex flex-col md:flex-row bg-white">
        <div className="flex-1 p-4 flex flex-col overflow-hidden border-r border-slate-200">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Source: Batch {moveSourceBatch?.batchNumber}</p>
          <div className="relative mb-3 shrink-0">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
            <input className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none" placeholder="Filter names..." value={moveSearch} onChange={e => setMoveSearch(e.target.value)} />
          </div>
          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-xl pr-1">
            {moveStudentsList.filter(s => `${s.firstName} ${s.lastName} ${s.usn || ''}`.toLowerCase().includes(moveSearch.toLowerCase())).map(s => (
              <div key={s.studentId} onClick={() => setSelectedStudents(prev => prev.includes(s.studentId) ? prev.filter(id => id !== s.studentId) : [...prev, s.studentId])}
                className={`p-3 border-b border-slate-100 cursor-pointer flex items-center gap-3 transition-colors ${selectedStudents.includes(s.studentId) ? 'bg-indigo-50/50' : 'hover:bg-slate-50/50'}`}>
                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selectedStudents.includes(s.studentId) ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 bg-white'}`}>
                  {selectedStudents.includes(s.studentId) && <FiCheckCircle className="text-white text-[10px]" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{s.firstName} {s.lastName}</p>
                  <p className="text-xs font-mono font-bold text-slate-400 mt-0.5">{s.usn || s.USN || ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full md:w-72 p-4 bg-slate-50 flex flex-col space-y-4 shrink-0">
          <div className="space-y-1.5">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Destination Group</p>
            <select className="w-full p-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none" value={moveTargetBatchId} onChange={e => setMoveTargetBatchId(e.target.value)}>
              <option value="">Select Target Group...</option>
              {labBatches.filter(b => b._id !== moveSourceBatch?._id && b.isActive).map(b => (
                <option key={b._id} value={b._id}>Batch {b.batchNumber} ({b.maxStudents - (b.studentIds?.length || 0)} open seats)</option>
              ))}
            </select>
          </div>
          <div className="mt-auto pt-4 border-t border-slate-200 bg-slate-100/50 p-3 rounded-xl border border-slate-200 text-center space-y-3">
            <p className="text-xs font-semibold text-slate-700">Moving <strong className="text-indigo-600 text-sm font-black">{selectedStudents.length}</strong> Selected Students</p>
            <button onClick={onConfirm} disabled={!moveTargetBatchId || selectedStudents.length === 0}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider disabled:opacity-40 transition-colors shadow-sm">
              Confirm Move
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const FacultyAssignmentModal = ({ batch, subjectName, availableFaculty, selectedFacultyIds, setSelectedFacultyIds, subjectFacultyMap, selectedSubjectId, batchId, handleUnassignFaculty, handleAssignFacultyToSubject, facultyLoading, onClose }) => {
  const batchSubjectKey = `${batchId}_${selectedSubjectId}`;
  const currentBatchFaculty = subjectFacultyMap[batchSubjectKey] || [];

  return (
    <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-[9999] flex items-center justify-center p-4">
      <div className="bg-slate-100 rounded-2xl w-full max-w-md flex flex-col max-h-[82vh] border border-slate-200 shadow-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-purple-50 shrink-0">
          <h3 className="font-bold text-sm text-purple-900 uppercase tracking-wide flex items-center gap-1.5"><FiUsers className="text-purple-700" /> Link Lab Teachers</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg border border-purple-200 bg-white text-purple-700"><FiX size={14} /></button>
        </div>
        <div className="flex-1 overflow-auto p-4 space-y-4 bg-white">
          <div className="bg-slate-50 border border-slate-200 p-3 rounded-xl text-xs font-semibold text-slate-700 space-y-0.5 shadow-xs">
            <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Target Group: {batch.batchPrefix} {batch.batchNumber}</p>
            <p className="text-sm font-bold text-slate-900 truncate">Subject: {subjectName}</p>
          </div>
          
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Select Teacher to Link</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto border border-slate-200 rounded-xl p-2 bg-slate-50/40 pr-1">
              {availableFaculty.length === 0 ? (
                <p className="text-center text-slate-400 text-xs py-6 font-medium">No mapped teachers available for this lab</p>
              ) : (
                availableFaculty.map(faculty => {
                  const uniqueId = faculty.facultyId || faculty.userId;
                  return (
                    <div key={uniqueId} onClick={() => setSelectedFacultyIds(prev => 
                      prev.includes(uniqueId) ? prev.filter(id => id !== uniqueId) : [...prev, uniqueId]
                    )} className={`p-2.5 cursor-pointer rounded-xl flex items-center gap-3 border transition-colors ${selectedFacultyIds.includes(uniqueId) ? 'bg-purple-50/70 border-purple-200 shadow-xs' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selectedFacultyIds.includes(uniqueId) ? 'bg-purple-600 border-purple-600' : 'border-slate-300 bg-white'}`}>
                        {selectedFacultyIds.includes(uniqueId) && <FiCheckCircle className="text-white text-[10px]" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-slate-800 truncate">{faculty.name}</p>
                        <p className="text-xs text-slate-500 font-medium truncate mt-0.5">{faculty.email}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {currentBatchFaculty.length > 0 && (
            <div className="space-y-1.5 pt-1">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Linked to This Group</p>
              <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                {currentBatchFaculty.map(facultyId => {
                  const faculty = availableFaculty.find(f => (f.facultyId || f.userId) === facultyId);
                  return faculty ? (
                    <div key={facultyId} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                      <div className="min-w-0 flex-1 pr-2">
                        <p className="font-bold text-slate-800 truncate">{faculty.name}</p>
                        <p className="text-[10px] font-medium text-slate-500 mt-0.5 truncate">{faculty.email}</p>
                      </div>
                      <button onClick={() => handleUnassignFaculty(batch._id, selectedSubjectId, facultyId)} className="w-7 h-7 flex items-center justify-center bg-rose-50 hover:bg-rose-100 border border-rose-100 rounded-lg text-rose-600 transition-colors flex-shrink-0">
                        <FiUserX size={13} />
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex gap-2.5 shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 bg-slate-200 hover:bg-slate-300 border border-slate-300 rounded-xl text-slate-700 font-bold text-xs uppercase tracking-wider transition-colors">Cancel</button>
          <button onClick={handleAssignFacultyToSubject} disabled={facultyLoading || selectedFacultyIds.length === 0}
            className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-1.5 shadow-sm transition-colors">
            {facultyLoading ? 'Assigning...' : 'Link to Batch'} <FiUserPlus size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LabBatchManagementModal;